import React, { useState } from 'react';
import { Smartphone, Chrome, Apple, Share, Plus, Home, Check, AlertCircle } from 'lucide-react';

export default function GuidePage() {
  const [activePlatform, setActivePlatform] = useState<'android' | 'ios'>('android');

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-black text-gray-900 mb-4">Installation Guide</h1>
        <p className="text-lg text-gray-600">
          Add this PWA to your home screen in just a few taps
        </p>
      </div>

      {/* Platform Selector */}
      <div className="flex gap-4 justify-center mb-8">
        <button
          onClick={() => setActivePlatform('android')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${
            activePlatform === 'android'
              ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg scale-105'
              : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-green-300'
          }`}
        >
          <Chrome className="w-5 h-5" />
          Android / Chrome
        </button>
        <button
          onClick={() => setActivePlatform('ios')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${
            activePlatform === 'ios'
              ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg scale-105'
              : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-300'
          }`}
        >
          <Apple className="w-5 h-5" />
          iOS / Safari
        </button>
      </div>

      {/* Android Guide */}
      {activePlatform === 'android' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                <Chrome className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-black text-gray-900">Android (Chrome)</h2>
            </div>
            <p className="text-gray-700 mb-6">
              Install this PWA on your Android device using Chrome browser
            </p>

            {/* Steps */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    1
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-900 mb-2">Open in Chrome</h3>
                    <p className="text-gray-600 mb-3">
                      Visit this website using Google Chrome browser on your Android device
                    </p>
                    <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                      <p className="text-sm text-green-800">
                        📱 Make sure you're using <strong>Chrome</strong>, not Samsung Internet or other browsers
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    2
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-900 mb-2">Tap the Menu (⋮)</h3>
                    <p className="text-gray-600 mb-3">
                      Tap the three-dot menu icon in the top-right corner of Chrome
                    </p>
                    <div className="bg-gray-100 p-4 rounded-lg flex items-center justify-center">
                      <div className="text-4xl">⋮</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    3
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-900 mb-2">Select "Add to Home screen"</h3>
                    <p className="text-gray-600 mb-3">
                      Look for the option that says "Add to Home screen" or "Install app"
                    </p>
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-4 rounded-lg flex items-center gap-3">
                      <Plus className="w-6 h-6" />
                      <span className="font-semibold">Add to Home screen</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    4
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-900 mb-2">Confirm Installation</h3>
                    <p className="text-gray-600 mb-3">
                      A popup will appear. Tap "Install" or "Add" to confirm
                    </p>
                    <div className="flex gap-3">
                      <div className="flex-1 bg-green-500 text-white p-3 rounded-lg text-center font-semibold">
                        Install
                      </div>
                      <div className="flex-1 bg-gray-200 text-gray-700 p-3 rounded-lg text-center font-semibold">
                        Cancel
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-green-500 text-white rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <Check className="w-8 h-8 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-lg mb-2">Done! 🎉</h3>
                    <p className="text-white/90">
                      The PWA icon will appear on your home screen. Tap it to open the app in full-screen mode!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Android Info */}
          <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
            <h3 className="font-bold text-lg text-gray-900 mb-3">💡 Additional Tips for Android</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-green-500">•</span>
                <span>Once installed, the PWA opens in full-screen without browser UI</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">•</span>
                <span>You can uninstall it like any other app (long-press → Uninstall)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">•</span>
                <span>The app will auto-update when you open it</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">•</span>
                <span>Works on Chrome, Edge, Samsung Internet (Android 5+)</span>
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* iOS Guide */}
      {activePlatform === 'ios' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border-2 border-blue-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                <Apple className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-black text-gray-900">iOS (Safari)</h2>
            </div>
            <p className="text-gray-700 mb-6">
              Install this PWA on your iPhone or iPad using Safari browser
            </p>

            {/* Steps */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    1
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-900 mb-2">Open in Safari</h3>
                    <p className="text-gray-600 mb-3">
                      Visit this website using Safari browser on your iPhone or iPad
                    </p>
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-800">
                        📱 Must use <strong>Safari</strong>. Chrome/Firefox on iOS don't support PWA installation
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    2
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-900 mb-2">Tap the Share Button</h3>
                    <p className="text-gray-600 mb-3">
                      Tap the Share icon at the bottom of Safari (box with arrow pointing up)
                    </p>
                    <div className="bg-gray-100 p-4 rounded-lg flex items-center justify-center">
                      <Share className="w-12 h-12 text-blue-500" />
                    </div>
                    <p className="text-sm text-gray-500 mt-2 text-center">
                      Usually located in the bottom center or bottom right
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    3
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-900 mb-2">Scroll and Find "Add to Home Screen"</h3>
                    <p className="text-gray-600 mb-3">
                      In the share menu, scroll down and tap "Add to Home Screen"
                    </p>
                    <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-4 rounded-lg flex items-center gap-3">
                      <Plus className="w-6 h-6" />
                      <span className="font-semibold">Add to Home Screen</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      💡 If you don't see it, scroll down in the share menu
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    4
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-900 mb-2">Customize Name (Optional)</h3>
                    <p className="text-gray-600 mb-3">
                      You can edit the app name if you want, then tap "Add" in the top-right
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <div className="text-sm text-gray-600 mb-2">App Name:</div>
                      <input 
                        type="text" 
                        value="PWA Demo" 
                        readOnly
                        className="w-full p-2 bg-white border border-gray-300 rounded"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-500 text-white rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <Check className="w-8 h-8 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-lg mb-2">All Set! 🎉</h3>
                    <p className="text-white/90">
                      The PWA icon will appear on your home screen. Tap it to launch the app!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional iOS Info */}
          <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
            <h3 className="font-bold text-lg text-gray-900 mb-3">💡 Additional Tips for iOS</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-blue-500">•</span>
                <span>PWA opens without Safari UI, looks like a native app</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500">•</span>
                <span>Delete like any app (long-press → Remove App)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500">•</span>
                <span>Requires iOS 11.3+ (iPhone 5s or newer)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500">•</span>
                <span>Push notifications supported on iOS 16.4+ (iPhone XR or newer)</span>
              </li>
            </ul>
          </div>

          {/* iOS Limitations Warning */}
          <div className="bg-amber-50 rounded-xl p-6 border-2 border-amber-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">⚠️ iOS Limitations</h3>
                <ul className="space-y-1 text-gray-700 text-sm">
                  <li>• PWAs can only be installed via Safari (not Chrome/Firefox)</li>
                  <li>• Push notifications require iOS 16.4+ (released March 2023)</li>
                  <li>• Some advanced features may be limited compared to Android</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* General Benefits */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border-2 border-purple-200 mt-8">
        <h2 className="text-2xl font-black text-gray-900 mb-4">Why Install as PWA?</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <Home className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-bold text-gray-900 mb-1">Home Screen Access</h4>
              <p className="text-gray-600 text-sm">Launch instantly from your home screen like a native app</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Smartphone className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-bold text-gray-900 mb-1">Full-Screen Experience</h4>
              <p className="text-gray-600 text-sm">No browser UI - looks and feels like a native app</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-2xl">🔔</span>
            <div>
              <h4 className="font-bold text-gray-900 mb-1">Push Notifications</h4>
              <p className="text-gray-600 text-sm">Receive notifications even when the app is closed</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-2xl">⚡</span>
            <div>
              <h4 className="font-bold text-gray-900 mb-1">Faster Loading</h4>
              <p className="text-gray-600 text-sm">Cached content loads instantly, even offline</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
